// Выбор одного значения в форме (components/Dropdown/README.md; ux.md, разделы 5, 12, 13):
// подпись над полем, кнопка в виде поля, меню под ней с галочкой у текущего значения и строкой-
// пояснением у каждого пункта. Ошибка и подсказка — одна строка под полем, как у Field.
// Пока вариантов нет (загрузка, сбой с кодом), меню показывает строку-сообщение (message).
import { ChevronDown } from 'lucide-react';
import type { ReactNode, Ref } from 'react';
import { cx } from '../lib/cx.ts';
import { Icon } from '../lib/icon.tsx';
import { Dropdown, DropdownRadioGroup, DropdownRadioItem } from './Dropdown.tsx';
import { Field } from './Field.tsx';
import type { FieldProps } from './Field.tsx';

export interface PickerOption<V extends string> {
  value: V;
  label: string;
  /** Строка-пояснение под названием пункта. */
  description?: string;
  disabled?: boolean;
}

export interface PickerFieldProps<V extends string> extends Omit<FieldProps, 'children'> {
  value: V | null;
  onChange: (value: V) => void;
  options: readonly PickerOption<V>[];
  /** Пустое значение приглашает к выбору приглушённым текстом. */
  placeholder?: string;
  /**
   * Строка-сообщение в меню над вариантами — DropdownMessage: загрузка, пустой список, сбой
   * с кодом и «Повторить». Не вариант с disabled: тот читается хуже 4.5:1.
   */
  message?: ReactNode;
  /** Первое поле окна: фокус при открытии окна — на эту кнопку (Modal ищет data-autofocus). */
  autoFocus?: boolean;
  ref?: Ref<HTMLButtonElement>;
}

export function PickerField<V extends string>({
  value,
  onChange,
  options,
  placeholder = 'Выбрать',
  message,
  autoFocus = false,
  ref,
  ...field
}: PickerFieldProps<V>) {
  const current = options.find((option) => option.value === value);
  return (
    <Field {...field}>
      {(control) => {
        const valueId = `${control.id}-value`;
        return (
          <Dropdown
            label={typeof field.label === 'string' ? field.label : undefined}
            className="kp-picker-menu"
            trigger={
              <button
                ref={ref}
                type="button"
                id={control.id}
                // Имя кнопки — подпись и текущее значение: «Роль Контролёр».
                aria-labelledby={`${control.id}-label ${valueId}`}
                aria-describedby={control['aria-describedby']}
                disabled={control.disabled}
                data-autofocus={autoFocus || undefined}
                className={cx(
                  't-input kp-picker',
                  !current && 'is-empty',
                  control['aria-invalid'] && 'is-error',
                )}
              >
                <span id={valueId} className="kp-picker-value">
                  {current?.label ?? placeholder}
                </span>
                <Icon icon={ChevronDown} className="t-ico" />
              </button>
            }
          >
            {message}
            <DropdownRadioGroup<string>
              value={value ?? ''}
              onValueChange={(next) => onChange(next as V)}
            >
              {options.map((option) => (
                <DropdownRadioItem
                  key={option.value}
                  value={option.value}
                  description={option.description}
                  disabled={option.disabled}
                >
                  {option.label}
                </DropdownRadioItem>
              ))}
            </DropdownRadioGroup>
          </Dropdown>
        );
      }}
    </Field>
  );
}
